<template>
  <div>
    <b-modal 
      ref="modal" 
      size="xl" 
      @hidden="reset" 
      centered 
      no-close-on-backdrop
      :hide-footer="true"
    >
    <template #modal-header="{ close }">
      <div class="d-flex justify-content-between align-items-center w-100">
        <h5 class="font-weight-bolder m-0">Finance Request Details</h5>
        <b-button
          size="sm"
          variant="none"
          class="btn-icon btn-circle btn-hover-dark"
          title="Close"
          @click="close"
          v-b-tooltip.hover
        >
          <i class="material-icons icon-xl">close</i>
        </b-button>
      </div>
    </template>

    <template #default>
      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <div class="spinner"></div>
        <span>Loading finance request details...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="alert alert-danger error-display">
        <div class="d-flex align-items-start">
          <i class="material-icons text-danger mr-2">error_outline</i>
          <div class="error-content">
            <strong>Error:</strong>
            <div class="error-message">{{ error }}</div>
            <small class="text-muted mt-1">
              If this issue persists, please contact support with the error details above.
            </small>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div v-else-if="financeRequest" class="finance-request-content">
        <!-- Basic Information Section -->
        <div class="info-section">
          <div class="info-grid">
            <div class="info-item">
              <label class="info-label">Invoice Number</label>
              <div class="info-value">{{ financeRequest.invoice_reference }}</div>
            </div>
            <div class="info-item status-item">
              <label class="info-label">Status</label>
              <span :class="['status-badge', statusBadgeClass(editableData.status)]">
                {{ formatStatusLabel(editableData.status) }}
              </span>
            </div>
            <div class="info-item">
              <label class="info-label">Total Receivable Amount</label>
              <div class="info-value" v-html="formatCurrency(financeRequest.total_receivable_amount, currency)"></div>
            </div>
            <div class="info-item">
              <label class="info-label">Total Disbursed Amount</label>
              <div class="info-value" v-html="formatCurrency(financeRequest.disbursement_amount, currency)"></div>
            </div>
            <div class="info-item">
              <label class="info-label">Transaction Fee</label>
              <div class="info-value" v-html="formatCurrency(financeRequest.transaction_fee, currency)"></div>
            </div>
            <div class="info-item">
              <label class="info-label">Settlement Fee</label>
              <div class="info-value" v-html="formatCurrency(financeRequest.settlement_fee, currency)"></div>
            </div>
            <div v-if="financeRequest.onboarding_fee && financeRequest.onboarding_fee > 0" class="info-item">
              <label class="info-label">Onboarding Fee</label>
              <div class="info-value" v-html="formatCurrency(financeRequest.onboarding_fee, currency)"></div>
            </div>
          </div>
        </div>

        <!-- Edit Controls Section -->
        <div class="edit-section">
          <h6 class="section-title">Update Request</h6>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label">Status</label>
                <select 
                  v-model="editableData.status" 
                  class="form-control status-select"
                  :disabled="saving || editableData.status === 'CLOSED'"
                >
                  <option value="DUE">Due</option>
                  <option value="PENDING_REVIEW">Pending Review</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="DISBURSED_AMOUNT">Disbursed Amount</option>
                  <option value="PARTIALLY_PAID">Partially Paid</option>
                  <option v-if="editableData.status === 'CLOSED'" value="CLOSED">Closed</option>
                </select>
              </div>
            </div>
            <div class="col-md-6" v-if="editableData.status === 'DISBURSED_AMOUNT'">
              <div class="form-group">
                <label class="form-label">Disbursed Date</label>
                <input 
                  v-model="editableData.disbursed_at" 
                  type="date" 
                  class="form-control"
                  :disabled="saving"
                />
              </div>
            </div>
          </div>

        </div>

        <!-- Purchased Receivables Section -->
        <div class="purchased-receivables-section">
          <h6 class="section-title">PURCHASED RECEIVABLES</h6>
          
          <div class="table-responsive">
            <table class="table table-bordered purchased-receivables-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Payer Name</th>
                  <th>Request Amount ({{ currency }})</th>
                  <th>Advance Rate</th>
                  <th>Eligible Amount ({{ currency }})</th>
                  <th>Payer Overlimit ({{ currency }})</th>
                  <th>Purchased Receivable ({{ currency }})</th>
                  <th>Tax Fee Rate %</th>
                  <th>Transaction Fee ({{ currency }})</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(invoice, index) in editableData.invoices" :key="invoice.id">
                  <td>
                    <a 
                      v-if="invoice.file" 
                      href="javascript:void(0)" 
                      @click="viewInvoiceFile(invoice)"
                      class="invoice-reference-link"
                      :title="`Click to view ${invoice.invoice_reference} PDF`"
                    >
                      {{ invoice.invoice_reference }}
                      <i class="las la-external-link-alt ml-1"></i>
                    </a>
                    <span v-else>{{ invoice.invoice_reference }}</span>
                  </td>
                  <td>{{ invoice.customer_name || 'N/A' }}</td>
                  <td class="text-right">{{ formatFullNumber(invoice.initial_requested_amount || invoice.invoice_amount) }}</td>
                  <td class="text-center">{{ invoice.advance_rate || 0 }}%</td>
                  <td class="text-right">{{ formatFullNumber(invoice.approval_amount || invoice.purchase_amount) }}</td>
                  <td class="text-right">{{ calculatePayerOverlimit(invoice) }}</td>
                  <td class="editable-cell">
                    <input 
                      type="number" 
                      v-model.number="editableData.invoices[index].purchase_amount"
                      :class="[
                        'form-control', 
                        'form-control-sm', 
                        'text-right',
                        { 'is-invalid': isPurchaseAmountExceeded(invoice) }
                      ]"
                      step="0.01"
                      :disabled="saving"
                      @input="updateTransactionFee(index)"
                      :max="invoice.approval_amount"
                    />
                    <div v-if="isPurchaseAmountExceeded(invoice)" class="invalid-feedback">
                      Exceeds eligible amount
                    </div>
                  </td>
                  <td class="text-center">{{ invoice.transaction_fees_rate || 0 }}</td>
                  <td class="text-right auto-calculated-cell">
                    <span class="calculated-value">{{ formatFullNumber(editableData.invoices[index].transaction_fees) }}</span>
                    <i class="las la-calculator calculated-icon" title="Auto-calculated"></i>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="total-row" :class="{ 'validation-error-row': hasValidationErrors }">
                  <td colspan="6" class="text-right font-weight-bold">Total</td>
                  <td class="text-right font-weight-bold">{{ formatFullNumber(totalPurchasedAmount) }}</td>
                  <td></td>
                  <td class="text-right font-weight-bold">{{ formatFullNumber(totalTransactionFees) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- Validation Messages -->
        <div v-if="purchaseAmountExceedsEligible" class="validation-message validation-error">
          <i class="las la-exclamation-triangle"></i>
          <span>
            Purchase amount for <strong>{{ purchaseAmountExceedsEligible.invoice_reference }}</strong> 
            ({{ formatFullNumber(purchaseAmountExceedsEligible.purchase_amount) }} {{ currency }}) 
            cannot exceed eligible amount 
            ({{ formatFullNumber(purchaseAmountExceedsEligible.approval_amount) }} {{ currency }})
          </span>
        </div>
        
        <div v-if="fundingValidationError && !purchaseAmountExceedsEligible" class="validation-message validation-warning">
          <i class="las la-exclamation-triangle"></i>
          <span>Total Purchased Receivable ({{ formatFullNumber(totalPurchasedAmount) }} {{ currency }}) must equal Total Receivable ({{ formatFullNumber(financeRequest.total_receivable_amount) }} {{ currency }})</span>
        </div>

        <!-- Action Buttons -->
        <div class="modal-footer-custom">
          <button 
            type="button" 
            class="btn btn-secondary" 
            @click="$refs.modal.hide()"
            :disabled="saving || initiatingPayment"
          >
            Cancel
          </button>
          
          <!-- Show Disbursed Invoice button for disbursed requests -->
          <button 
            type="button" 
            class="btn btn-info ml-2" 
            @click="viewDisbursedInvoice"
            :disabled="loadingInvoice"
          >
            <span v-if="loadingInvoice" class="spinner-border spinner-border-sm mr-2"></span>
            <i v-else class="material-icons mr-1">description</i>
            {{ loadingInvoice ? 'Loading...' : 'Generate & Save Invoice' }}
          </button>
          
          <!-- Show Save Changes for other statuses OR for newly approved requests -->
          <button 
            type="button" 
            class="btn btn-primary" 
            @click="saveChanges"
            :disabled="!hasChanges || saving || hasValidationErrors"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm mr-2"></span>
            {{ saving ? 'Saving...' : 'Save Changes' }}
          </button>
          
          <!-- Show Pay Now button for approved requests -->
          <button 
            v-if="editableData.status === 'APPROVED' && !hasChanges"
            type="button" 
            class="btn btn-success ml-2" 
            @click="navigateToPaymentPage"
            :disabled="saving"
          >
            <i class="material-icons mr-1">payment</i>
            Pay Now
          </button>
        </div>
      </div>
    </template>
    </b-modal>
      
      <pdf-viewer 
        v-if="showPDF" 
        :url="pdfUrl" 
        :name="`Financing Invoice-${financeRequest.invoice_reference}.pdf`"
        :showHeader="true"
        :forceExpansion="true" 
        :enableOverlayEffect="true" 
        @closePDF="closePDFViewer"
      />

  </div>  
</template>

<script>
import FinancingService from '@/services/financing.service';
import { extractApiErrorMessage } from '@/lib/helpers.js';
import PdfViewer from '@/components/pdfViewer.vue';
import './FinanceRequestModal.scss';

export default {
  name: 'FinanceRequestModal',
  components: {
    PdfViewer
  },
  props: {
    currency: {
      type: String,
      default: 'USD'
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      initiatingPayment: false,
      loadingInvoice: false,
      error: null,
      financeRequest: null,
      editableData: {
        status: '',
        invoices: [],
        disbursed_at: null
      },
      originalData: null,
      showPDF: false,
      pdfUrl: null
    };
  },
  computed: {
    totalFundedAmount() {
      if (!this.editableData.invoices) return 0;
      return this.editableData.invoices.reduce((total, invoice) => {
        return total + (parseFloat(invoice.funded_amount) || 0);
      }, 0);
    },
    totalPurchasedAmount() {
      if (!this.editableData.invoices) return 0;
      return this.editableData.invoices.reduce((total, invoice) => {
        return total + (parseFloat(invoice.purchase_amount) || 0);
      }, 0);
    },
    totalTransactionFees() {
      if (!this.editableData.invoices) return 0;
      return this.editableData.invoices.reduce((total, invoice) => {
        return total + (parseFloat(invoice.transaction_fees) || 0);
      }, 0);
    },
    maxAllowedFundedAmount() {
      // No longer needed, but keep for compatibility if referenced elsewhere
      if (!this.financeRequest) return 0;
      return this.totalFundedAmount;
    },
    fundingValidationError() {
      // Validate that total purchased amount equals total receivable amount
      if (!this.financeRequest || !this.editableData.invoices) return false;
      
      const totalReceivable = parseFloat(this.financeRequest.total_receivable_amount) || 0;
      const totalPurchased = this.totalPurchasedAmount;
      
      // Allow a small margin for floating point errors (0.01)
      if (Math.abs(totalReceivable - totalPurchased) > 0.01) {
        return true;
      }
      
      return false;
    },
    purchaseAmountExceedsEligible() {
      // Check if any purchase amount exceeds its eligible amount
      if (!this.editableData.invoices) return null;
      
      const exceededInvoice = this.editableData.invoices.find(invoice => {
        const purchaseAmount = parseFloat(invoice.purchase_amount) || 0;
        const eligibleAmount = parseFloat(invoice.approval_amount) || 0;
        return purchaseAmount > eligibleAmount;
      });
      
      return exceededInvoice || null;
    },
    hasValidationErrors() {
      return this.fundingValidationError || this.purchaseAmountExceedsEligible;
    },
    hasChanges() {
      if (!this.originalData) return false;
      // Check status changes
      if (this.editableData.status !== this.originalData.status) {
        return true;
      }
      // Check disbursed_at changes when status is DISBURSED_AMOUNT
      if (this.editableData.status === 'DISBURSED_AMOUNT' && 
          this.editableData.disbursed_at !== this.originalData.disbursed_at) {
        return true;
      }
      // Check for invoice changes (purchased amounts)
      if (JSON.stringify(this.editableData.invoices) !== JSON.stringify(this.originalData.invoices)) {
        return true;
      }
      return false;
    }
  },
  methods: {
    extractErrorMessage(error) {
      return extractApiErrorMessage(error);
    },

    updateTransactionFee(index) {
      // Recalculate transaction fee based on purchase amount and rate
      const invoice = this.editableData.invoices[index];
      if (invoice && invoice.transaction_fees_rate) {
        const purchaseAmount = parseFloat(invoice.purchase_amount) || 0;
        const feeRate = parseFloat(invoice.transaction_fees_rate) || 0;
        // Calculate fee: purchase_amount * (rate / 100)
        invoice.transaction_fees = purchaseAmount * (feeRate / 100);
        
        // Force reactivity update
        this.$set(this.editableData.invoices, index, invoice);
      }
    },

    isPurchaseAmountExceeded(invoice) {
      const purchaseAmount = parseFloat(invoice.purchase_amount) || 0;
      const eligibleAmount = parseFloat(invoice.approval_amount) || 0;
      return purchaseAmount > eligibleAmount;
    },

    calculatePayerOverlimit(invoice) {
      const purchaseAmount = parseFloat(invoice.purchase_amount);
      const approvalAmount = parseFloat(invoice.approval_amount);
      
      // If either value doesn't exist or is invalid, return "-"
      if (isNaN(purchaseAmount) || isNaN(approvalAmount)) {
        return '-';
      }
      
      const overlimit = Math.abs(purchaseAmount - approvalAmount);
      
      // If the result is 0 or negative (no overlimit), return "-"
      if (overlimit <= 0) {
        return '-';
      }
      
      // Otherwise, return the formatted overlimit amount
      return ('(' + this.formatFullNumber(overlimit) + ')');
    },

    async open(requestId) {
      this.reset();
      this.$refs.modal.show();
      await this.loadFinanceRequest(requestId);
    },
    
    calculateInitialFundedAmounts(invoices) {
      // Map invoices with all necessary fields for the new structure
      return invoices.map(invoice => ({
        ...invoice,
        funded_amount: invoice.funded_amount !== undefined && invoice.funded_amount !== null 
          ? parseFloat(invoice.funded_amount) || 0
          : parseFloat(invoice.invoice_amount) || 0,
        purchase_amount: invoice.purchase_amount !== undefined && invoice.purchase_amount !== null
          ? parseFloat(invoice.purchase_amount) || 0
          : parseFloat(invoice.approval_amount || invoice.invoice_amount) || 0,
        initial_requested_amount: invoice.initial_requested_amount !== undefined && invoice.initial_requested_amount !== null
          ? parseFloat(invoice.initial_requested_amount)
          : parseFloat(invoice.invoice_amount) || 0,
        advance_rate: invoice.advance_rate || 0,
        transaction_fees_rate: invoice.transaction_fees_rate || 0,
        transaction_fees: invoice.transaction_fees !== undefined && invoice.transaction_fees !== null
          ? parseFloat(invoice.transaction_fees) || 0
          : 0,
        approval_amount: invoice.approval_amount !== undefined && invoice.approval_amount !== null
          ? parseFloat(invoice.approval_amount)
          : parseFloat(invoice.purchase_amount) || 0
      }));
    },

    initializeDisbursedAt() {
      // Check if disbursed_at exists in additional_fields
      if (this.financeRequest && 
          this.financeRequest.additional_fields && 
          this.financeRequest.additional_fields.disbursed_at) {
        // Convert to date format (YYYY-MM-DD) if it's a valid date
        const disbursedDate = new Date(this.financeRequest.additional_fields.disbursed_at);
        if (!isNaN(disbursedDate.getTime())) {
          return disbursedDate.toISOString().slice(0, 10);
        }
      }
      
      // Fallback to current date if not available
      const now = new Date();
      return now.toISOString().slice(0, 10);
    },
    
    async loadFinanceRequest(requestId) {
      try {
        this.loading = true;
        this.error = null;
        
        const response = await FinancingService.getFinanceRequest(requestId);
        this.financeRequest = response.data;
        
        // Initialize editable data with max funded amounts
        this.editableData = {
          status: this.financeRequest.status || '',
          invoices: this.financeRequest.invoices ? this.calculateInitialFundedAmounts(this.financeRequest.invoices) : [],
          disbursed_at: this.initializeDisbursedAt()
        };
        
        // Store original data for comparison
        this.originalData = JSON.parse(JSON.stringify(this.editableData));
        
        // No automatic status checking - payment status will be updated when modal is reopened
        
      } catch (error) {
        console.error('Error loading finance request:', error);
        this.error = this.extractErrorMessage(error);
      } finally {
        this.loading = false;
      }
    },
    
    async saveChanges() {
      try {
        this.saving = true;
        this.error = null;
        const updateData = {};
        
        // Check if status changed
        if (this.editableData.status !== this.originalData.status) {
          updateData.status = this.editableData.status;
        }

        // Include disbursed_at when status is DISBURSED_AMOUNT
        if (this.editableData.status === 'DISBURSED_AMOUNT' && this.editableData.disbursed_at) {
          // Convert date format to ISO string
          const disbursedDate = new Date(this.editableData.disbursed_at);
          if (!isNaN(disbursedDate.getTime())) {
            updateData.disbursed_at = disbursedDate.toISOString();
          }
        }
        
        // Check if invoices (purchased amounts or transaction fees) changed
        if (JSON.stringify(this.editableData.invoices) !== JSON.stringify(this.originalData.invoices)) {
          // Send updated invoice data matching FundedInvoiceSerializer format
          updateData.invoices = this.editableData.invoices.map(invoice => ({
            id: invoice.id,
            purchase_amount: (parseFloat(invoice.purchase_amount) || 0).toFixed(2),
            transaction_fees: (parseFloat(invoice.transaction_fees) || 0).toFixed(2)
          }));
          
        }
        
        // Add business ID to the payload if available
        const businessIdFromUrl = this.$route.params.business_id;
        if (businessIdFromUrl) {
          updateData.business_id = businessIdFromUrl;
        } else if (this.financeRequest.borrower_id) {
          updateData.business_id = this.financeRequest.borrower_id;
        } else if (this.financeRequest.business_id) {
          updateData.business_id = this.financeRequest.business_id;
        }
        
        console.log('Update payload:', updateData);
        
        await FinancingService.updateFinanceRequest(this.financeRequest.id, updateData);
        this.$showAlert('Finance request updated successfully', 'success');
        this.$emit('updated', this.financeRequest.id);
        this.$refs.modal.hide();
      } catch (error) {
        console.error('Error saving finance request:', error);
        this.error = this.extractErrorMessage(error);
      } finally {
        this.saving = false;
      }
    },
    

    
    formatStatusLabel(status) {
      const statusLabels = {
        'PENDING_REVIEW': 'Pending Review',
        'APPROVED': 'Approved', 
        'REJECTED': 'Rejected',
        'DISBURSED_AMOUNT': 'Disbursed Amount',
        "CLOSED": "Closed",
        "PARTIALLY_PAID": "Partially Paid",
        "DUE": "Due",
      };
      return statusLabels[status] || status;
    },
    
    getCurrencySymbol(currency) {
      const symbols = {
        'USD': '$',
        'EUR': '€',
        'GBP': '£',
        'AED': 'د.إ',
        'SAR': 'ر.س'
      };
      return symbols[currency] || currency;
    },
    
    getFundingPercentage(invoice) {
      // No longer needed
      return 100;
    },
    
    getMaxFundedAmountForInvoice(invoice, currentIndex) {
      // No longer needed
      return invoice.invoice_amount || 0;
    },
    
    getRoundedFundedAmount(invoice) {
      // No longer needed
      return parseFloat(invoice.invoice_amount) || 0;
    },
    
    formatCurrency(amount, currency = 'USD') {
      if (!amount || isNaN(amount)) return this.$formatCurrency(0, currency);
      
      // Format the amount and add currency as superscript
      const formatted = this.$formatCurrency(amount, currency);
      // Remove the currency from the formatted string since we'll add it as superscript
      const amountOnly = formatted.replace(/[A-Z]{3}\s?/, '');
      return `${amountOnly}<sup>${currency}</sup>`;
    },

    formatFullNumber(val) {
      if (val === undefined || val === null || isNaN(val)) return '-';
      return Number(val).toLocaleString('en-US', { 
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    
    statusBadgeClass(status) {
      if (!status) return 'status-default';
      const s = status.toLowerCase();
      if (s === 'approved' || s === 'funded') return 'status-approved';
      if (s === 'pending') return 'status-pending';
      if (s === 'rejected') return 'status-rejected';
      if (s === 'processing') return 'status-processing';
      if (s === 'pending_review') return 'status-pending_review';
      if (s === 'disbursed_amount') return 'status-disbursed_amount';
      if (s === 'closed') return 'status-closed';
      return 'status-' + s.replace(/[^a-z0-9_-]/g, '') || 'status-default';
    },
    
    navigateToPaymentPage() {
      // Navigate to the dedicated payment page
      this.$router.push({
        name: 'zand-payment',
        params: { requestId: this.financeRequest.id }
      });
    },
    
    getBankDetails() {
      if (!this.financeRequest || !this.financeRequest.bank_details) {
        return { account_holder_name: 'N/A', account_iban: 'N/A' };
      }
      try {
        const bankDetails = typeof this.financeRequest.bank_details === 'string' 
          ? JSON.parse(this.financeRequest.bank_details) 
          : this.financeRequest.bank_details;
        return {
          account_holder_name: bankDetails.account_holder_name || 'N/A',
          account_iban: bankDetails.account_iban || 'N/A'
        };
      } catch (error) {
        console.error('Error parsing bank details:', error);
        return { account_holder_name: 'N/A', account_iban: 'N/A' };
      }
    },
    
    
    async viewDisbursedInvoice() {
      try {
        this.loadingInvoice = true;
        
        const pdfBlob = await FinancingService.getDisbursementInvoice(this.financeRequest.id);
        
        // Create a blob URL for the PDF
        this.pdfUrl = URL.createObjectURL(pdfBlob);
        
        // Show the PDF modal
        this.showPDF = true;
        
      } catch (error) {
        console.error('Error loading disbursed invoice:', error);
        this.$showAlert(
          this.extractErrorMessage(error) || 'Failed to load disbursed invoice. Please try again.',
          'error'
        );
      } finally {
        this.loadingInvoice = false;
      }
    },
    
    async viewInvoiceFile(invoice) {
      if (!invoice.file) {
        this.$showAlert('No file available for this invoice', 'warning');
        return;
      }
      
      try {
        // Fetch the PDF file as a blob
        const response = await fetch(invoice.file);
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF file: ${response.status} ${response.statusText}`);
        }
        
        const blob = await response.blob();
        
        // Create a blob URL for the PDF viewer
        this.pdfUrl = URL.createObjectURL(blob);
        
        // Show the PDF viewer
        this.showPDF = true;
        
      } catch (error) {
        console.error('Error loading invoice PDF:', error);
        this.$showAlert('Failed to load invoice PDF. Opening in new tab instead.', 'warning');
        // Fallback to opening in new tab
        window.open(invoice.file, '_blank');
      }
    },
    
    closePDFViewer() {
      this.showPDF = false;
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = null;
      }
    },
    
    reset() {
      this.loading = false;
      this.saving = false;
      this.initiatingPayment = false;
      this.loadingInvoice = false;
      this.error = null;
      this.financeRequest = null;
      this.editableData = {
        status: '',
        invoices: [],
        disbursed_at: null
      };
      this.originalData = null;
      
      // Clean up PDF viewer
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = null;
      }
      this.showPDF = false;
      // No payment data to reset anymore
    }
  }
};
</script> 